import { ListItemText, MenuItem, Select, TextField, FormControl } from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useContext, useState } from "react";
import DateLocalizationProvider from "../../../../../components/element/DateLocalizationProvider";
import SearchAircraftDialog from "../../../../../components/element/SearchAircraftDialog";
import { StorageContext } from "../../../../../services/StorageProvider";
import { AirportType, DATE_FORMAT, TIME_FORMAT } from "../../../../../utils/constants";
import "../CreateFlightPlan.css";
import SearchAirportDialog from "../../../../../components/element/SearchAirportDialog";
import { useFormData } from "../../../../../helpers/FormDataProvider";
import SearchAirlineDialog from "../../../../../components/element/SearchAirlineDialog";
import { getAirportData } from "../../../../../utils/utils";

dayjs.extend(utc);

const CreateFlightPlanTable = ({
  flightDetails,
  airlineData,
  setFlightDetails,
  selectedAirline,
  setSelectedAirline,
  selectedOrigin,
  setSelectedOrigin,
  selectedDestination,
  setSelectedDestination,
}) => {
  const { userData } = useFormData();
  const [arrivalData, setArrivalData] = useState({});
  const [departureData, setDepartureData] = useState({});
  const [departureFromArrivalData, setDepartureFromArrivalData] = useState({});
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const { airportData } = useContext(StorageContext);
  const [showSerachAircraftDialog, setShowSerachAircraftDialog] =
    useState(false);
  const [showSerachAirportDialog, setShowSerachAirportDialog] = useState(false);
  const [showSerachAirlineDialog, setShowSerachAirlineDialog] = useState(false);
  const [airportUpdatingType, setAirportUpdatingType] = useState("");
  const [selectedDepartureTerminal, setSelectedDepartureTerminal] = useState("");
  const [selectedArrivalTerminal, setSelectedArrivalTerminal] = useState("");

  const handleInputChange = (key) => (event) => {
    setFlightDetails({
      ...flightDetails,
      [key]: key === "aircraftRegistrationNumber" ? event.target.value.toUpperCase() : event.target.value,
    });
  };
  const handleFlightNumberInputChange = (event) => {
    setFlightDetails({
      ...flightDetails,
      flightNumber: event.target.value,
      iataFlightNumber: event.target.value,
      icaoFlightNumber: event.target.value,
    });
  };

  const handleAirlineChange = (event) => {
    const selectedId = event.target.value;
    const selectedAirline = airlineData?.data?.find(
      (airline) => airline.id === selectedId
    );
    setSelectedAirline(selectedAirline);
    setFlightDetails({
      ...flightDetails,
      airlineId: selectedAirline.id,
    });
  };

  const handleSearchAirlineChange = (airline) => {
    setSelectedAirline(airline);
    setFlightDetails({
      ...flightDetails,
      airlineId: airline.id,
    });
  };

  const handleDepartureDateChange = (newValue) => {
    setDepartureData((prev) => ({
      ...prev,
      date: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      departureTime: getDateAndTime(newValue, departureData?.time),
    });
  };

  const handleDepartureTimeChange = (newValue) => {
    setDepartureData((prev) => ({
      ...prev,
      time: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      departureTime: getDateAndTime(departureData?.date, newValue),
    });
  };

  const handleArrivalDateChange = (newValue) => {
    setArrivalData((prev) => ({
      ...prev,
      date: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      arrivalTime: getDateAndTime(newValue, arrivalData?.time),
    });
  };

  const handleArrivalTimeChange = (newValue) => {
    setArrivalData((prev) => ({
      ...prev,
      time: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      arrivalTime: getDateAndTime(arrivalData?.date, newValue),
    });
  };

  const handleDepartureDateFromArrivalAirport = (newValue) => {
    setDepartureFromArrivalData((prev) => ({
      ...prev,
      date: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      departureTimeFromArrivalAirport: getDateAndTime(
        newValue,
        departureFromArrivalData?.time
      ),
    });
  };

  const handleDepartureTimeFromArrivalAirport = (newValue) => {
    setDepartureFromArrivalData((prev) => ({
      ...prev,
      time: newValue,
    }));
    setFlightDetails({
      ...flightDetails,
      departureTimeFromArrivalAirport: getDateAndTime(
        departureFromArrivalData?.date,
        newValue
      ),
    });
  };

  const handleAircraftOnSelect = (aircraft) => {
    setSelectedAircraft(aircraft);
    setFlightDetails({
      ...flightDetails,
      aircraftTypeId: aircraft?.id,
    });
  };

  const handleSelectAirportClick = (type) => {
    setAirportUpdatingType(type);
    setShowSerachAirportDialog(true);
  };

  const handleAirportOnSelect = (airport) => {
    if (airportUpdatingType === "origin") {
      setSelectedOrigin(airport);
      // Clear departure terminal when origin changes
      setSelectedDepartureTerminal("");
      const { departureAirportTerminalId, ...rest } = flightDetails;
      setFlightDetails({
        ...rest,
        departureAirportId: airport.id,
      });
    } else {
      setSelectedDestination(airport);
      // Clear arrival terminal when destination changes
      setSelectedArrivalTerminal("");
      const { arrivalAirportTerminalId, ...rest } = flightDetails;
      setFlightDetails({
        ...rest,
        arrivalAirportId: airport.id,
      });
    }
  };

  // Helper function to get airport data by ID (using common utils)
  const getAirportById = (airportId) => getAirportData(airportData, airportId);

  // Helper function to check if airport is DOM type
  const isDOMAirport = (airportId) => {
    const airport = getAirportById(airportId);
    return airport?.airportType === AirportType.DOMESTIC;
  };

  // Handle terminal selection for departure
  const handleDepartureTerminalChange = (event) => {
    const terminalId = event.target.value;
    setSelectedDepartureTerminal(terminalId);

    // Only add terminal ID to flightDetails if a terminal is selected
    if (terminalId && terminalId !== "") {
      setFlightDetails({
        ...flightDetails,
        departureAirportTerminalId: terminalId,
      });
    } else {
      // Remove the key if no terminal is selected
      const { departureAirportTerminalId, ...rest } = flightDetails;
      setFlightDetails(rest);
    }
  };

  // Handle terminal selection for arrival
  const handleArrivalTerminalChange = (event) => {
    const terminalId = event.target.value;
    setSelectedArrivalTerminal(terminalId);

    // Only add terminal ID to flightDetails if a terminal is selected
    if (terminalId && terminalId !== "") {
      setFlightDetails({
        ...flightDetails,
        arrivalAirportTerminalId: terminalId,
      });
    } else {
      // Remove the key if no terminal is selected
      const { arrivalAirportTerminalId, ...rest } = flightDetails;
      setFlightDetails(rest);
    }
  };

  const previousMonthStart = dayjs().subtract(1, "month").startOf("month");

  const getDateAndTime = (newDate, newTime) => {
    if (newDate && newTime) {
      const localDateTime = dayjs(newDate)
        .hour(newTime.hour())
        .minute(newTime.minute());
      const utcDateTime = localDateTime.utc();
      return utcDateTime.format("YYYY-MM-DDTHH:mm:ss[Z]");
    } else {
      return null;
    }
  };

  return (
    <DateLocalizationProvider>
      <div className="create-flight-plan-table-parent-container">
        <div className="create-flight-plan-table-container">
          <table className="create-flight-plan-table">
            <thead>
              <tr>
                <th>Aircraft Type</th>
                <th>Engine Type</th>
                <th>Engine Count</th>
                <th>MTOW</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div
                    className="create-flight-plan-select-aircraft"
                    onClick={() => setShowSerachAircraftDialog(true)}
                  >
                    {selectedAircraft ? (
                      <p>{selectedAircraft?.icaoCode}</p>
                    ) : (
                      <span className="placeholder-text">Search</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="create-flight-plan-non-editable-container">
                    {selectedAircraft?.engineType}
                  </div>
                </td>
                <td>
                  <div className="create-flight-plan-non-editable-container">
                    {selectedAircraft?.engineCount}
                  </div>
                </td>
                <td>
                  <div className="create-flight-plan-non-editable-container">
                    {selectedAircraft?.maxTakeOffWeight}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="create-flight-plan-table-container">
          <table className="create-flight-plan-table">
            <thead>
              <tr>
                <th className="width-140">Airline</th>
                <th className="width-140">Flight No.</th>
                <th className="width-140">Tail Number</th>
                <th className="width-260">Origin</th>
                <th className="width-260">Destination</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {userData?.isAAUser ? (
                    <div
                      className="create-flight-plan-select-airline"
                      onClick={() => setShowSerachAirlineDialog(true)}
                    >
                      <p>{selectedAirline?.airlineName}</p>
                    </div>
                  ) : (
                    <Select
                      id="create-flight-plan-airline"
                      className="create-flight-plan-select-container"
                      value={selectedAirline.id || ""}
                      onChange={handleAirlineChange}
                      displayEmpty
                      renderValue={(selected) =>
                        selected ? (
                          airlineData?.data?.find(
                            (airline) => airline?.id === selected
                          )?.airlineName
                        ) : (
                          <span className="placeholder-text">Select</span>
                        )
                      }
                    >
                      {airlineData?.data?.map((airline) => (
                        <MenuItem key={airline?.id} value={airline?.id}>
                          <ListItemText primary={airline?.airlineName} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </td>
                <td>
                  <TextField
                    id="create-flight-plan-flight-no"
                    className="create-flight-plan-textfield"
                    inputProps={{
                      autoComplete: "off",
                      maxLength: 15,
                    }}
                    value={flightDetails.flightNo}
                    onChange={handleFlightNumberInputChange}
                    onKeyDown={(e) => {
                      // block non-numeric keys at typing stage
                      if (
                        !/[0-9]/.test(e.key) && // allow only digits
                        e.key !== "Backspace" &&
                        e.key !== "Delete" &&
                        e.key !== "ArrowLeft" &&
                        e.key !== "ArrowRight" &&
                        e.key !== "Tab"
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                </td>
                <td>
                  <TextField
                    id="create-flight-plan-tail-no"
                    className="create-flight-plan-textfield"
                    inputProps={{
                      autoComplete: "off",
                      maxLength: 15,
                    }}
                    value={flightDetails.aircraftRegistrationNumber}
                    onChange={handleInputChange("aircraftRegistrationNumber")}
                  />
                </td>
                <td>
                  <div className="airport-terminal-container">
                    <div
                      className="create-flight-plan-select-airport"
                      onClick={() => handleSelectAirportClick("origin")}
                    >
                      {selectedOrigin?.name ? (
                        <p>{selectedOrigin?.name}</p>
                      ) : (
                        <span className="placeholder-text">Search</span>
                      )}
                    </div>
                    {selectedOrigin?.id && isDOMAirport(selectedOrigin.id) && (
                      <FormControl size="small" className="terminal-dropdown-container">
                        <Select
                          value={selectedDepartureTerminal}
                          onChange={handleDepartureTerminalChange}
                          displayEmpty
                          variant="outlined"
                          className="terminal-dropdown"
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {getAirportById(selectedOrigin.id)?.terminals?.map((terminal) => (
                            <MenuItem key={terminal.id} value={terminal.id}>
                              {terminal.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </div>
                </td>
                <td>
                  <div className="airport-terminal-container">
                    <div
                      className="create-flight-plan-select-airport"
                      onClick={() => handleSelectAirportClick("destination")}
                    >
                      {selectedDestination?.name ? (
                        <p>{selectedDestination?.name}</p>
                      ) : (
                        <span className="placeholder-text">Search</span>
                      )}
                    </div>
                    {selectedDestination?.id && isDOMAirport(selectedDestination.id) && (
                      <FormControl size="small" className="terminal-dropdown-container">
                        <Select
                          value={selectedArrivalTerminal}
                          onChange={handleArrivalTerminalChange}
                          displayEmpty
                          variant="outlined"
                          className="terminal-dropdown"
                        >
                          <MenuItem value="">
                            <em>Select</em>
                          </MenuItem>
                          {getAirportById(selectedDestination.id)?.terminals?.map((terminal) => (
                            <MenuItem key={terminal.id} value={terminal.id}>
                              {terminal.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="create-flight-plan-table-container">
          <table className="create-flight-plan-table">
            <thead>
              <tr>
                <th className="width-200">Depart Date and Time</th>
                <th className="width-200">Arrival Date and Time</th>
                <th className="width-280">
                  Departure From The Arrival Airport Date and Time
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="create-flight-plan-picker-container">
                    <MobileDatePicker
                      id="create-flight-plan-departure-date"
                      className="create-flight-plan-datePicker"
                      format={DATE_FORMAT}
                      minDate={previousMonthStart}
                      maxDate={arrivalData?.date}
                      value={departureData?.date}
                      onChange={handleDepartureDateChange}
                    />
                    <MobileTimePicker
                      id="create-flight-plan-departure-time"
                      className="create-flight-plan-timePicker"
                      format={TIME_FORMAT}
                      value={departureData?.time}
                      onChange={handleDepartureTimeChange}
                    />
                  </div>
                </td>
                <td>
                  <div className="create-flight-plan-picker-container">
                    <MobileDatePicker
                      id="create-flight-plan-arrival-date"
                      className="create-flight-plan-datePicker "
                      format={DATE_FORMAT}
                      minDate={departureData?.date ?? previousMonthStart}
                      value={arrivalData?.date}
                      onChange={handleArrivalDateChange}
                    />
                    <MobileTimePicker
                      id="create-flight-plan-arrival-time"
                      className="create-flight-plan-timePicker"
                      format={TIME_FORMAT}
                      value={arrivalData?.time}
                      onChange={handleArrivalTimeChange}
                    />
                  </div>
                </td>
                <td>
                  <div className="create-flight-plan-picker-container">
                    <MobileDatePicker
                      id="create-flight-plan-departure-from-arrival-date"
                      className="create-flight-plan-datePicker "
                      format={DATE_FORMAT}
                      minDate={arrivalData?.date ?? previousMonthStart}
                      value={departureFromArrivalData?.date}
                      onChange={handleDepartureDateFromArrivalAirport}
                    />
                    <MobileTimePicker
                      id="create-flight-plan-departure-from-arrival-time"
                      className="create-flight-plan-timePicker"
                      format={TIME_FORMAT}
                      value={departureFromArrivalData?.time}
                      onChange={handleDepartureTimeFromArrivalAirport}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <SearchAircraftDialog
          open={showSerachAircraftDialog}
          onClose={() => setShowSerachAircraftDialog(false)}
          onSelectAircraft={handleAircraftOnSelect}
        />
        <SearchAirportDialog
          open={showSerachAirportDialog}
          onClose={() => setShowSerachAirportDialog(false)}
          airportData={airportData}
          onSelectAirport={handleAirportOnSelect}
        />
        <SearchAirlineDialog
          open={showSerachAirlineDialog}
          onClose={() => setShowSerachAirlineDialog(false)}
          onSelectAirline={handleSearchAirlineChange}
        />
      </div>
    </DateLocalizationProvider>
  );
};

export default CreateFlightPlanTable;
